import { Component, OnInit, HostListener, ViewChild, ElementRef, AfterViewInit, OnDestroy, inject } from '@angular/core';
import { CartService } from '../cart.service';
import { CartItem, Cart, CartValidation, AusPostShippingService } from '@redirector-service/shared';
import { Observable, lastValueFrom, map } from 'rxjs';
import { Router, RouterLink } from '@angular/router';
import { HttpStatusCode } from '@angular/common/http';
import { MetaDataService } from '../metadata.service';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { StripeAddressElement } from '@stripe/stripe-js';
import { environment } from '../../environments/environment';
import { AlertService } from '../alert.service';
import { IShippingAddress } from '@redirector-service/shared';
import { NGXLogger } from 'ngx-logger';
import { NgTemplateOutlet, AsyncPipe, CurrencyPipe, NgOptimizedImage } from '@angular/common';
import { HomeCarouselComponent } from '../home-carousel/home-carousel.component';
import { ImageModifierPipe } from '../image-modifer.pipe';

// TODO : Refactor this interface into the right place
interface IShippingAddressVerification {
  addressComplete: boolean;
  hasUnconfirmedComponents: boolean;
}

@Component({
    selector: 'tea-shop-fe-cart-view',
    templateUrl: './cart-view.component.html',
    styleUrls: ['./cart-view.component.css'],
    animations: [
        trigger('expandCollapse', [
            state('collapsed', style({
                height: '0',
                opacity: '0',
                overflow: 'hidden',
                padding: '0'
            })),
            state('expanded', style({
                height: '*',
                opacity: '1'
            })),
            transition('collapsed <=> expanded', [
                animate('300ms ease-in-out')
            ])
        ])
    ],
    imports: [NgTemplateOutlet, RouterLink, HomeCarouselComponent, AsyncPipe, CurrencyPipe, ImageModifierPipe, NgOptimizedImage]
})
export class CartViewComponent implements OnInit, AfterViewInit, OnDestroy {
  private cartService = inject(CartService);
  private router = inject(Router);
  private metaDataService = inject(MetaDataService);
  private alertService = inject(AlertService);
  private logger = inject(NGXLogger);

  cart$!: Observable<Cart>;
  total$!: Observable<number>;
  cartValidation: CartValidation | null = null;
  isValidating = false;
  isCartExpanded = true;
  isLargeScreen = window.innerWidth >= 1024; // 1024px is the 'lg' breakpoint in Tailwind
  isValidatingAddress = false;

  verifiedShippingAddress: IShippingAddress | null = null;

  availableShippingServices: AusPostShippingService[] = [];
  isLoadingShippingServices = false;
  shippingServicesNeedsUpdate = true;

  @ViewChild('shippingAddress') shippingAddressRef!: ElementRef;
  shippingAddressElement?: StripeAddressElement;

  @HostListener('window:resize')
  onResize() {
    this.isLargeScreen = window.innerWidth >= 1024;
  }

  constructor() { 
    this.metaDataService.updateMetadata({
      title: 'Shopping Cart',
      image: '/assets/contact/sencha-logo.png',
    });
  }

  ngOnInit() {
    this.cart$ = this.cartService.getCart();
    this.total$ = this.cart$.pipe(
      map(cart => 
        cart.totalAmount
      )
    );
    const currentShippingService = this.getSelectedShippingService();
    this.availableShippingServices = currentShippingService ? [currentShippingService] : [];
  }

  ngOnDestroy() {
    if (this.shippingAddressElement) {
      this.shippingAddressElement.destroy();
    }
  }

  ngAfterViewInit() {
    if (!this.shippingAddressRef) {
      this.logger.trace('Shipping address element not found. Nothing in cart?');
      return;
    }
    const cartShippingAddress = this.cartService.getShippingAddress();
    const stripeElements = this.cartService.stripe.elements();
    this.shippingAddressElement = stripeElements.create('address', {
      mode: 'shipping',
      autocomplete: {
        mode: 'google_maps_api',
        apiKey: environment.GOOGLE_MAPS_API_KEY,
      },
      defaultValues: cartShippingAddress,
    });
    this.shippingAddressElement.mount(this.shippingAddressRef.nativeElement);
    this.shippingAddressElement.once('change', () => {
      this.logger.trace('Shipping address element ready');
      if (this.cartService.getShippingAddress()) {
        this.alertService.show({
          title: 'Shipping Address Found',
          description: 'A shipping address has been found. Loading shipping services.',
          type: 'success'
        });
        this.updateAvailableShippingServices()
        .then(() => {
          const selectedService = this.getSelectedShippingService();
          if (selectedService) {
            const validSelection = !!this.availableShippingServices.find(service => service.serviceCode === selectedService?.serviceCode);
            if (validSelection) {
              // If the current selection matches an available service, keep it
              // and set it in the cart again in case we need to recalculate the price
              this.cartService.setShippingService(selectedService);
            }
          }
        });
      }  
    });
  }

  getShippingAddress() {
    return this.cartService.getShippingAddress();
  }

  clearShippingAddress() {
    this.cartService.clearShippingAddress();
  }


  getSelectedShippingService() {
    return this.cartService.getShippingService();
  }

  selectShippingService(service: AusPostShippingService) {
    this.cartService.setShippingService(service);
  }

  /**
   * Revalidates both the shipping address and the available shipping services.
   * 
   */
  async revalidateShippingDetails() {
    // Revalidate the shipping address using validateShippingAddressEx then update the available shipping service
    this.isValidatingAddress = true;
    try {
      const result = await this.shippingAddressElement?.getValue();
      if (result?.complete) {
        const unvalidatedShippingAddress: IShippingAddress = result.value as IShippingAddress;
        const verification = await this.validateShippingAddress(unvalidatedShippingAddress);
        if (!verification) {
          throw new Error('Address verification failed');
        }
        this.verifiedShippingAddress = verification.addressComplete ? unvalidatedShippingAddress : null;
        if (!verification.addressComplete) {
          // Handle incomplete address
          this.logger.error('Shipping address could not be validated');
          const confirmAddress = confirm('We were not able to verify your shipping address. Would you like to use it and proceed anyway?');
          if (!confirmAddress) {
            this.logger.warn('User cancelled address verification');
            return;
          } else {
            this.logger.warn('User confirmed address verification');
          }
        } else if (verification.hasUnconfirmedComponents) {
          // Handle unconfirmed address components
          this.logger.warn('Shipping address may not be complete');
          this.alertService.show({
            title: 'Address May Not Be Complete',
            description: 'The address looks valid, but may not be complete.\n\nPlease review your address before proceeding.\n\nIf you are sure the address is complete, you can proceed.',
            type: 'warning',
            duration: 20000,
          });
        } else {
          // Handle valid address
          this.logger.log('Shipping address is valid');
          this.alertService.show({
            title: 'Address is Valid',
            description: 'The address is valid and ready for shipping.',
            type: 'success'
          });
        }
        await this.cartService.setShippingAddress(unvalidatedShippingAddress);
        this.updateAvailableShippingServices();
      }
    } catch (error) {
      this.logger.error('Error revalidating address:', error);
      this.alertService.show({
        title: 'Error Revalidating Address',
        description: 'There was an error revalidating the address. Please try again.',
        type: 'error'
      });
    } finally {
      this.isValidatingAddress = false;
    }
  }

  async updateAvailableShippingServices() {
    this.isLoadingShippingServices = true;
    try {
      const services = await lastValueFrom(this.cartService.getAvailableShippingServices());
      services.sort((a, b) => a.price - b.price);
      this.availableShippingServices = services;
      this.alertService.show({
        title: 'Shipping Services Updated',
        description: 'The available shipping services have been updated for your review.',
        type: 'info'
      });
    } catch (error) {
      this.logger.error('Error loading shipping services:', error);
      this.alertService.show({
        title: 'Error Loading Shipping Services',
        description: 'There was an error loading the shipping services. Please try again.',
        type: 'error'
      });
    } finally {
      this.isLoadingShippingServices = false;
    }
  }

  async validateShippingAddress(unvalidatedShippingAddress: IShippingAddress) {
    try {
      const verification = await lastValueFrom<IShippingAddressVerification>(this.cartService.verifyShippingAddress(unvalidatedShippingAddress));
      return verification;
    } catch (error) {
      this.logger.error('Error validating address:', unvalidatedShippingAddress, error);
      return null;
    }
  }

  updateQuantity(item: CartItem, change: number) {
    const updatedItem = { ...item, quantity: Math.max(0, item.quantity + change) };
    const shippingService = this.cartService.getShippingService();
    this.cartService.setCartItem(updatedItem);
    if (!this.getShippingAddress()) {
      this.logger.log('Quantity updated, but shipping address is missing. Not refreshing shipping services.');
      return;
    }
    this.updateAvailableShippingServices()
    .then(() => {
      if (shippingService) {
        const validSelection = !!this.availableShippingServices.find(service => service.serviceCode === shippingService?.serviceCode);
        if (validSelection) {
          // If the current selection matches an available service, keep it
          // and set it in the cart again in case we need to recalculate the price
          this.cartService.setShippingService(shippingService);
        }
      }
    });
  }

  removeItem(item: CartItem) {
    const shippingService = this.cartService.getShippingService();
    this.cartService.removeFromCart(item);
    this.updateAvailableShippingServices()
    .then(() => {
      if (shippingService) {
        const validSelection = !!this.availableShippingServices.find(service => service.serviceCode === shippingService?.serviceCode);
        if (validSelection) {
          // If the current selection matches an available service, keep it
          // and set it in the cart again in case we need to recalculate the price
          this.cartService.setShippingService(shippingService);
        }
      }
    });
  }

  async validateCart() {
    this.logger.log('validatg car');

    if (!this.cartService.getShippingAddress()) {
      this.logger.log('Shipping address is missing');
      this.alertService.show({
        title: 'Shipping Address is Incomplete',
        description: 'Please review your shipping address before proceeding.',
        type: 'warning'
      });
      return;
    }
    if (!this.cartService.getShippingService()) {
      this.logger.log('Shipping service is missing');
      this.alertService.show({
        title: 'Shipping Service Not Selected',
        description: 'Please select a shipping service before proceeding.',
        type: 'warning'
      });
      return;
    }
    this.logger.log('Validating cart...');
    this.isValidating = true;
    this.cartService.validateCart().subscribe({
      next: (validation: CartValidation) => {
        this.cartValidation = validation;
        this.isValidating = false;
        this.logger.log('Cart is valid');
        this.goToCheckout();
      },
      error: (error) => {
        this.isValidating = false;
        if (error.status === HttpStatusCode.BadRequest) {
          this.cartValidation = error.error;
          this.logger.log('Cart validation failed:', error.error);
          alert('Your cart has 1 or more errors and failed a security check. Please review the cart and try again.');
        } else {
          this.logger.error('An error occurred while validating the cart:', error);
          alert('An error occurred while validating the cart. Please try again shortly.');
        }
      }
    });
  }

  goToCheckout(): void {
    this.router.navigate(['/checkout']);
  }

  getAnimationState(): string {
    return (!this.isCartExpanded && !this.isLargeScreen) ? 'collapsed' : 'expanded';
  }
}
